import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import QuoteSubmissionForm from './components/QuoteSubmissionForm';
import ProjectSpecifications from './components/ProjectSpecifications';
import ClientCommunication from './components/ClientCommunication';
import { ToastContainer, useToast } from '../../components/ui/Toast';
import { projectService } from '../../services/projectService';
import { quoteService } from '../../services/quoteService';

const VendorProjectDetails = () => {
  const { projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [existingQuote, setExistingQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [quoteSuccessData, setQuoteSuccessData] = useState(null);
  const { toasts, addToast, removeToast } = useToast();

  // Get projectId from route param or query string
  const projectId = routeProjectId || searchParams?.get('projectId');

  const vendorData = {
    vendorId: searchParams?.get('vendorId') || null,
    businessName: searchParams?.get('vendorName') ? decodeURIComponent(searchParams?.get('vendorName')) : (searchParams?.get('vendor') || 'Your Business'),
    email: searchParams?.get('vendorEmail') ? decodeURIComponent(searchParams?.get('vendorEmail')) : null,
  };

  // Fetch real project data from Supabase
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setLoadError('No project ID provided.');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const data = await projectService?.getByIdPublic(projectId);
        setProjectData(data);

        // Also fetch existing quotes to check if this vendor already submitted
        try {
          const quotes = await quoteService?.getByProjectId(projectId);
          const vendorEmail = vendorData?.email;
          const vendorName = vendorData?.businessName;
          // Find a quote matching this vendor (by vendorId or vendor name)
          const vendorQuote = quotes?.find(q =>
            (vendorData?.vendorId && q?.vendorId === vendorData?.vendorId) ||
            (q?.vendor?.name && vendorName && q?.vendor?.name?.toLowerCase() === vendorName?.toLowerCase())
          );
          if (vendorQuote) setExistingQuote(vendorQuote);
        } catch (qErr) {
          // Non-critical — vendor can still submit
          console.warn('Could not fetch existing quotes:', qErr);
        }
      } catch (err) {
        console.error('Failed to load project:', err);
        setLoadError('Could not load project details. The link may be invalid or expired.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  // Calculate time remaining from deadline
  useEffect(() => {
    if (!projectData?.responseDeadline) return;
    const calculateTimeRemaining = () => {
      const deadline = new Date(projectData?.responseDeadline);
      const now = new Date();
      const timeDiff = deadline - now;
      if (timeDiff > 0) {
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining({ days, hours, minutes });
      } else {
        setTimeRemaining({ expired: true });
      }
    };
    calculateTimeRemaining();
    const timer = setInterval(calculateTimeRemaining, 60000);
    return () => clearInterval(timer);
  }, [projectData?.responseDeadline]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const handleSubmitQuote = () => {
    setIsQuoteFormOpen(true);
  };

  const handleQuoteSuccess = (submittedDetails) => {
    setIsQuoteFormOpen(false);
    setExistingQuote({ status: 'submitted' }); // Optimistic update
    setQuoteSuccessData(submittedDetails || { submittedAt: new Date()?.toISOString() });
    addToast('Quote submitted successfully! The buyer will be notified.', 'success');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'specifications', label: 'Specifications', icon: 'Settings' },
    { id: 'communication', label: 'Communication', icon: 'MessageSquare' }
  ];

  // Build rich project data from Supabase response
  const specs = projectData?.specifications || {};
  const quoteSettings = specs?.quoteSettings || {};
  const projectPhotos = projectData?.photos || [];
  const hasSubmittedQuote = existingQuote?.status === 'submitted' || existingQuote?.status === 'accepted';

  // Build requirements list from specs
  const requirementsList = (() => {
    if (Array.isArray(specs?.needs)) return specs?.needs;
    if (specs?.needs && typeof specs?.needs === 'object') return Object.values(specs?.needs);
    if (Array.isArray(quoteSettings?.requirements)) return quoteSettings?.requirements;
    if (projectData?.description) return [projectData?.description];
    return [];
  })();

  // Build specifications object for the Specifications tab
  const specsForTab = (() => {
    const result = {};
    const excluded = ['weightings', 'quoteSettings', 'needs', 'isOnlineOnly', 'options', 'purchaseType', 'timeline', 'notes', 'budget', 'budgetMin', 'budgetMax', 'budgetRange', 'budget_min', 'budget_max', 'priceMax', 'priceMin', 'price'];
    // First, flatten the nested `specs` sub-object (where DynamicSpecsForm fields are stored)
    const nestedSpecs = specs?.specs || {};
    Object.entries(nestedSpecs)?.forEach(([k, v]) => {
      if (k === 'weightings') return; // always skip weightings
      if (!excluded?.includes(k) && v != null && v !== '' && typeof v !== 'object') {
        result[k] = v;
      } else if (!excluded?.includes(k) && Array.isArray(v) && v?.length > 0) {
        result[k] = v;
      } else if (!excluded?.includes(k) && v != null && v !== '' && typeof v === 'boolean') {
        result[k] = v;
      }
    });
    // Then add top-level spec fields (excluding the nested specs object itself and excluded keys)
    Object.entries(specs)?.forEach(([k, v]) => {
      if (k === 'specs') return; // already flattened above
      if (k === 'weightings') return; // always skip weightings
      if (!excluded?.includes(k) && v != null && v !== '' && typeof v !== 'object') {
        result[k] = v;
      } else if (!excluded?.includes(k) && Array.isArray(v) && v?.length > 0) {
        result[k] = v;
      } else if (!excluded?.includes(k) && v != null && v !== '' && typeof v === 'boolean') {
        result[k] = v;
      }
    });
    return result;
  })();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-text-secondary">Loading project details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (loadError || !projectData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 flex items-center justify-center">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
            <h2 className="text-xl font-bold text-foreground mb-2">Project Not Found</h2>
            <p className="text-text-secondary mb-6">{loadError || 'This project could not be loaded.'}</p>
            <Button variant="default" onClick={() => navigate('/')}>Go Home</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32">
        {/* Project Header */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-success/5 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Status Badge */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-success/10 text-success text-sm font-medium">
                  <Icon name="CheckCircle" size={14} className="mr-1" />
                  Registered Vendor
                </div>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Icon name="Crown" size={14} className="mr-1" />
                  Premium Stack
                </div>
              </div>
              
              {/* Time Remaining */}
              {timeRemaining && !timeRemaining?.expired && (
                <div className="bg-warning/10 text-warning px-4 py-2 rounded-lg border border-warning/20">
                  <div className="flex items-center text-sm font-medium">
                    <Icon name="Clock" size={16} className="mr-2" />
                    {timeRemaining?.days}d {timeRemaining?.hours}h {timeRemaining?.minutes}m remaining
                  </div>
                </div>
              )}
              {timeRemaining?.expired && (
                <div className="bg-error/10 text-error px-4 py-2 rounded-lg border border-error/20">
                  <div className="flex items-center text-sm font-medium">
                    <Icon name="AlertCircle" size={16} className="mr-2" />
                    Deadline passed
                  </div>
                </div>
              )}
            </div>

            {/* Project Title */}
            <div className="mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                {projectData?.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-text-secondary">
                <div className="flex items-center">
                  <Icon name="Tag" size={18} className="mr-2" />
                  <span className="font-medium">{projectData?.category}</span>
                </div>
                {projectData?.location && projectData?.location !== 'Location not specified' && (
                  <div className="flex items-center">
                    <Icon name="MapPin" size={18} className="mr-2" />
                    <span className="font-medium">{projectData?.location}</span>
                  </div>
                )}
                {projectData?.responseDeadline && (
                  <div className="flex items-center">
                    <Icon name="Calendar" size={18} className="mr-2" />
                    <span className="font-medium">Deadline: {formatDate(projectData?.responseDeadline)}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <Icon name="Users" size={18} className="mr-2" />
                  <span className="font-medium">{projectData?.vendorCount || 0} vendors invited</span>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Icon name="Clock" size={18} className="mr-2 text-primary" />
                  <h3 className="font-semibold text-foreground text-sm">Timeline</h3>
                </div>
                <p className="text-lg font-bold text-primary">{specs?.timeline || projectData?.timeline || 'Not specified'}</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Icon name="AlertCircle" size={18} className="mr-2 text-warning" />
                  <h3 className="font-semibold text-foreground text-sm">Quote Deadline</h3>
                </div>
                <p className="text-base font-bold text-warning">{formatDate(projectData?.responseDeadline)}</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Icon name="FileText" size={18} className="mr-2 text-primary" />
                  <h3 className="font-semibold text-foreground text-sm">Quote Status</h3>
                </div>
                <p className={`text-base font-bold ${hasSubmittedQuote ? 'text-success' : 'text-warning'}`}>
                  {hasSubmittedQuote ? 'Submitted ✓' : 'Pending'}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="default"
                size="lg"
                onClick={handleSubmitQuote}
                iconName="FileText"
                iconPosition="left"
                className="bg-primary hover:bg-primary/90"
                disabled={hasSubmittedQuote}
              >
                {hasSubmittedQuote ? 'Quote Submitted ✓' : 'Submit Quote & Proposal'}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => setActiveTab('communication')}
              >
                Message Client
              </Button>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </section>

        {/* Tab Content */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Project Description */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h2 className="text-xl font-bold text-foreground mb-4 flex items-center">
                      <Icon name="FileText" size={20} className="mr-2" />
                      Stack Description
                    </h2>
                    <p className="text-text-secondary leading-relaxed">
                      {projectData?.description || specs?.notes || 'No description provided.'}
                    </p>
                  </div>

                  {/* Key Project Specs (from DynamicSpecsForm) */}
                  {Object.keys(specsForTab)?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="ClipboardList" size={20} className="mr-2" />
                        Project Details
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(specsForTab)?.map(([key, value]) => (
                          <div key={key} className="flex items-start space-x-3 p-3 bg-surface rounded-lg border border-border">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-0.5">
                                {key?.replace(/([A-Z])/g, ' $1')?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())?.trim()}
                              </p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {Array.isArray(value) ? value?.join(', ') : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project Photos */}
                  {projectPhotos?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="Image" size={20} className="mr-2" />
                        Project Photos
                      </h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {projectPhotos?.map((photo, idx) => {
                          // Photos stored as objects with url (Supabase Storage) or dataUrl (legacy base64)
                          const src = typeof photo === 'string' ? photo : photo?.url || photo?.dataUrl || null;
                          if (!src) return null;
                          return (
                            <div key={idx} className="relative">
                              <img
                                src={src}
                                alt={typeof photo === 'object' && photo?.name ? photo?.name : `Project photo ${idx + 1}`}
                                className="w-full h-40 object-cover rounded-lg border border-border"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div
                                className="w-full h-40 rounded-lg border border-border bg-surface items-center justify-center flex-col gap-2 text-text-secondary text-xs"
                                style={{ display: 'none' }}
                              >
                                <Icon name="ImageOff" size={24} />
                                <span>Photo unavailable</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Requirements */}
                  {requirementsList?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="CheckSquare" size={20} className="mr-2" />
                        Stack Requirements
                      </h2>
                      <div className="space-y-3">
                        {requirementsList?.map((requirement, index) => (
                          <div key={index} className="flex items-start">
                            <Icon name="CheckCircle" size={16} className="mr-3 mt-1 text-success flex-shrink-0" />
                            <span className="text-text-secondary">{requirement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quote Instructions */}
                  {(quoteSettings?.instructions || quoteSettings?.requirements?.length > 0) && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="Info" size={20} className="mr-2" />
                        Quote Instructions
                      </h2>
                      {quoteSettings?.instructions && (
                        <p className="text-text-secondary mb-4 leading-relaxed">{quoteSettings?.instructions}</p>
                      )}
                      {Array.isArray(quoteSettings?.requirements) && quoteSettings?.requirements?.length > 0 && (
                        <div className="space-y-2">
                          {quoteSettings?.requirements?.map((req, i) => (
                            <div key={i} className="flex items-start">
                              <Icon name="ArrowRight" size={14} className="mr-2 mt-1 text-primary flex-shrink-0" />
                              <span className="text-text-secondary text-sm">{req}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Client Information */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
                      <Icon name="UserCheck" size={18} className="mr-2" />
                      Client Information
                    </h2>
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">Name</h3>
                        <p className="text-text-secondary">{projectData?.clientName || 'Client'}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">Posted</h3>
                        <p className="text-text-secondary">{formatDate(projectData?.postedDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Competition Info */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
                      <Icon name="BarChart3" size={18} className="mr-2" />
                      Competition Overview
                    </h2>
                    <div className="space-y-4">
                      <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <div className="text-2xl font-bold text-primary mb-1">{projectData?.vendorCount || 0}</div>
                        <div className="text-sm text-text-secondary">Vendors Invited</div>
                      </div>
                    </div>
                  </div>

                  {/* Quotesheet Preview */}
                  {quoteSettings?.quotesheetActive && quoteSettings?.quotesheet?.sections?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
                        <Icon name="ClipboardList" size={18} className="mr-2" />
                        Quote Sheet Preview
                      </h2>
                      <p className="text-sm text-text-secondary mb-3">Fields you'll need to complete:</p>
                      <div className="space-y-3">
                        {quoteSettings?.quotesheet?.sections?.map((section, si) => (
                          <div key={si}>
                            <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">{section?.title}</p>
                            <div className="space-y-1">
                              {section?.fields?.map((field, fi) => (
                                <div key={fi} className="flex items-center text-sm text-text-secondary">
                                  <Icon name="Circle" size={6} className="mr-2 text-primary flex-shrink-0" />
                                  {field?.label}{field?.required && <span className="text-error ml-1">*</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full mt-4"
                        onClick={handleSubmitQuote}
                        disabled={hasSubmittedQuote}
                      >
                        {hasSubmittedQuote ? 'Quote Submitted ✓' : 'Fill Out Quote Sheet'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <ProjectSpecifications specifications={specsForTab} />
            )}

            {activeTab === 'communication' && (
              <ClientCommunication 
                clientData={{
                  name: projectData?.clientName,
                  email: null, // Don't expose client email directly
                  phone: null
                }}
                projectId={projectId}
              />
            )}
          </div>
        </section>

        {/* Quote Submission Modal */}
        {isQuoteFormOpen && (
          <QuoteSubmissionForm
            isOpen={isQuoteFormOpen}
            onClose={() => setIsQuoteFormOpen(false)}
            onSuccess={handleQuoteSuccess}
            projectData={{
              id: projectId,
              title: projectData?.title,
              name: projectData?.title,
              description: projectData?.description || specs?.notes,
              ownerId: null,
              clientEmail: null, // fetched server-side on quote submit
              clientName: projectData?.clientName,
              quoteSettings: {
                ...(specs?.quoteSettings || {}),
                quotesheetActive: specs?.quoteSettings?.quotesheetActive ?? false,
                quotesheet: specs?.quoteSettings?.quotesheet || null,
              },
              specifications: specsForTab,
              photos: projectPhotos,
              existingQuoteId: existingQuote?.id || null,
            }}
            vendorData={vendorData}
          />
        )}

        {/* Quote Success Banner */}
        {quoteSuccessData && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <div className="fixed inset-0 bg-black/50" onClick={() => setQuoteSuccessData(null)} />
              <div className="relative z-10 bg-background rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                {/* Success Header */}
                <div className="bg-gradient-to-br from-success/20 via-success/10 to-primary/10 px-6 py-8 text-center border-b border-border">
                  <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={36} className="text-success" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Quote Submitted!</h2>
                  <p className="text-text-secondary text-sm">
                    Your quote has been successfully submitted to the client.
                  </p>
                </div>

                {/* Quote Details */}
                <div className="px-6 py-6 space-y-4">
                  <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Submission Summary</h3>
                  <div className="bg-surface rounded-xl border border-border divide-y divide-border">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center space-x-2 text-text-secondary text-sm">
                        <Icon name="Building2" size={15} />
                        <span>Vendor</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{quoteSuccessData?.vendorName || vendorData?.businessName}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center space-x-2 text-text-secondary text-sm">
                        <Icon name="FolderOpen" size={15} />
                        <span>Project</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{quoteSuccessData?.projectName || projectData?.title}</span>
                    </div>
                    {quoteSuccessData?.totalAmount && (
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center space-x-2 text-text-secondary text-sm">
                          <Icon name="DollarSign" size={15} />
                          <span>Total Amount</span>
                        </div>
                        <span className="text-sm font-bold text-success">${Number(quoteSuccessData?.totalAmount)?.toLocaleString()}</span>
                      </div>
                    )}
                    {quoteSuccessData?.timeline && (
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center space-x-2 text-text-secondary text-sm">
                          <Icon name="Clock" size={15} />
                          <span>Timeline</span>
                        </div>
                        <span className="text-sm font-medium text-foreground">{quoteSuccessData?.timeline}</span>
                      </div>
                    )}
                    {quoteSuccessData?.warranty && (
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center space-x-2 text-text-secondary text-sm">
                          <Icon name="Shield" size={15} />
                          <span>Warranty</span>
                        </div>
                        <span className="text-sm font-medium text-foreground">{quoteSuccessData?.warranty}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center space-x-2 text-text-secondary text-sm">
                        <Icon name="Calendar" size={15} />
                        <span>Submitted</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {quoteSuccessData?.submittedAt
                          ? new Date(quoteSuccessData?.submittedAt)?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : 'Just now'}
                      </span>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-4">
                    <h3 className="text-sm font-semibold text-primary mb-3 flex items-center">
                      <Icon name="ListChecks" size={15} className="mr-2" />
                      What Happens Next
                    </h3>
                    <ul className="space-y-2">
                      {[
                        { icon: 'Bell', text: 'The client will be notified of your submission.' },
                        { icon: 'BarChart3', text: 'Your quote will be compared alongside other vendors.' },
                        { icon: 'MessageSquare', text: 'The client may reach out with follow-up questions.' },
                        { icon: 'CheckCircle', text: 'You\'ll be notified if your quote is selected.' },
                      ]?.map((step, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-text-secondary">
                          <Icon name={step?.icon} size={14} className="mt-0.5 text-primary flex-shrink-0" />
                          <span>{step?.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6">
                  <Button
                    variant="default"
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={() => setQuoteSuccessData(null)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default VendorProjectDetails;